import L, { DivIcon, LatLngExpression } from "leaflet";
import React, { useMemo } from "react";
import ReactDOMServer from "react-dom/server";
import { Marker, Tooltip } from "react-leaflet";

type CircularMarkerProps = {
  position: LatLngExpression;
  color: string;
  label: string | number;
  tooltip?: React.ReactNode;
  size?: number;
  borderColor?: string;
  borderWidth?: number;
  textColor?: string;
  fontSize?: number;
  zIndexOffset?: number;
  onClick?: () => void;
  title?: string;
  className?: string;
};

const CircularMarker: React.FC<CircularMarkerProps> = ({
  position,
  color,
  label,
  tooltip,
  size = 36,
  borderColor = "#ffffff",
  borderWidth = 2,
  textColor = "#000",
  fontSize = 10,
  zIndexOffset = 0,
  onClick,
  title,
  className = "",
}) => {
  /** Memoize the icon so it only recalculates when appearance props change */
  const icon: DivIcon = useMemo(() => {
    const diameter = size;
    const radius = diameter / 2;

    const view = ReactDOMServer.renderToStaticMarkup(
      <div
        className={`circular-marker ${className}`}
        style={{
          width: `${diameter}px`,
          height: `${diameter}px`,
          lineHeight: `26px`,
          backgroundColor: color,
          border: `${borderWidth}px solid ${borderColor}`,
          color: textColor,
          fontSize: `${fontSize}px`,
          textAlign: "center",
          borderRadius: "50%",
          fontWeight: 700,
          boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
          userSelect: "none",
          boxSizing: "border-box",
        }}
        title={title}
      >
        {label}
      </div>
    );

    return L.divIcon({
      html: view,
      className: "",
      iconSize: [diameter, diameter],
      iconAnchor: [radius, radius],
      tooltipAnchor: [0, -radius],
    });
  }, [
    className,
    color,
    label,
    size,
    borderColor,
    borderWidth,
    textColor,
    fontSize,
    title,
  ]);

  return (
    <Marker
      position={position}
      icon={icon}
      zIndexOffset={zIndexOffset}
      eventHandlers={onClick ? { click: onClick } : undefined}
    >
      {tooltip ? (
        <Tooltip direction="top" offset={[0, -size / 2]} opacity={1}>
          <div style={{ minWidth: 120 }}>{tooltip}</div>
        </Tooltip>
      ) : null}
    </Marker>
  );
};

export default CircularMarker;
